import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, BarChart3, PieChart as PieChartIcon } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { API_URL } from '../lib/apiBase';

const COLORS = {
  build_next: '#3b82f6',  // blue
  fix_now: '#f59e0b',      // amber
  ignore: '#94a3b8'        // gray
};

const TrendCharts = () => {
  const { currentWorkspaceId } = useWorkspace();
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentWorkspaceId) fetchTrends();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspaceId]);

  const fetchTrends = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analytics/trends`, {
        params: { days: 7, workspace_id: currentWorkspaceId },
      });
      setTrendData(response.data);
    } catch (error) {
      console.error('Error fetching trends:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!trendData || !trendData.trends) {
    return null;
  }

  const { timeline, category_breakdown } = trendData.trends;
  const segmentStats = trendData.segment_stats || {};

  // Prepare pie chart data for lanes
  const laneData = [
    { name: 'Build Next', value: timeline.reduce((sum, d) => sum + d.build_next, 0), color: COLORS.build_next },
    { name: 'Fix Now', value: timeline.reduce((sum, d) => sum + d.fix_now, 0), color: COLORS.fix_now },
    { name: 'Ignore', value: timeline.reduce((sum, d) => sum + d.ignore, 0), color: COLORS.ignore }
  ];

  // Prepare segment stats for bar chart
  const segmentChartData = Object.entries(segmentStats).map(([segment, stats]) => ({
    segment: segment.charAt(0).toUpperCase() + segment.slice(1),
    decisions: stats.count,
    revenue: stats.total_revenue_potential
  }));

  return (
    <div className="space-y-6" data-testid="trend-charts">
      <div>
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Analytics & Trends
        </h2>
        <p className="text-sm text-muted-foreground">Signal patterns and insights over time</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Timeline Chart */}
        <Card className="p-6 lg:col-span-2">
          <h3 className="text-sm font-semibold mb-4">Signals Over Time</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                stroke="#6b7280"
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                stroke="#6b7280"
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="build_next" 
                stroke={COLORS.build_next} 
                strokeWidth={2}
                name="Build Next"
              />
              <Line 
                type="monotone" 
                dataKey="fix_now" 
                stroke={COLORS.fix_now} 
                strokeWidth={2}
                name="Fix Now"
              />
              <Line 
                type="monotone" 
                dataKey="ignore" 
                stroke={COLORS.ignore} 
                strokeWidth={2}
                name="Ignore"
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Lane Distribution Pie Chart */}
        <Card className="p-6">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
            <PieChartIcon className="h-4 w-4" />
            Decision Distribution
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={laneData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {laneData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Breakdown */}
        <Card className="p-6">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Categories
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={category_breakdown}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="category" 
                tick={{ fontSize: 11 }}
                stroke="#6b7280"
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                stroke="#6b7280"
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="count" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Segment Stats */}
        {segmentChartData.length > 0 && (
          <Card className="p-6">
            <h3 className="text-sm font-semibold mb-4">Signals by Segment</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={segmentChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="segment" 
                  tick={{ fontSize: 12 }}
                  stroke="#6b7280"
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  stroke="#6b7280"
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar dataKey="decisions" fill="#8b5cf6" name="Decisions" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TrendCharts;
